import React, { useState, useEffect, useCallback } from 'react';
import { features, cartLabels } from '@/data/content';
import { useStorageEvent, STORAGE_EVENT_DATE } from '@/hooks/useStorageEvent';
import { useToast } from '@/Context/ToastContext';
import { useAddToBasket } from '@/hooks/useAddToBasket';
import { trackEvent } from '@/Services/analytics';
import { menuItems } from '@/data/foods';
import api from '@/Services/api';
import { API } from '@/Services/apiConfig';
import AddToBasketCompact from '@/components/ui/AddToBasketCompact';
import AddToBasketFull from '@/components/ui/AddToBasketFull';
import Calendar from '@/components/ui/Calendar';
import Button from '@/components/ui/Button';
import { SpinnerIcon } from '@/components/ui/Icons';

export interface AddToBasketProps {
    foodId?: number;
    menuId?: number;
    minQuantity?: number;
    maxQuantity?: number;
    price: number;
    isAvailable?: boolean;
    compact?: boolean;
}

const AddToBasket: React.FC<AddToBasketProps> = ({
    foodId,
    menuId,
    minQuantity = 1,
    maxQuantity: propsMaxQuantity,
    price,
    isAvailable = true,
    compact = false,
}) => {
    if ((!foodId && !menuId) || (foodId && menuId)) {
        return null;
    }

    const id = foodId ?? menuId!;
    const maxQuantity = propsMaxQuantity ?? 99;
    const [quantity, setQuantity] = useState(minQuantity);
    const [showCalendar, setShowCalendar] = useState(false);
    const [tempSelectedDate, setTempSelectedDate] = useState<Date | null>(null);
    const [availableDates, setAvailableDates] = useState<Set<string> | undefined>(undefined);
    const [loadingDates, setLoadingDates] = useState(false);
    const { showToast } = useToast();

    const foodData = foodId ? menuItems.find((f) => f.id === foodId) : undefined;
    const productName = foodData?.item_name || foodData?.name || `Product ${id}`;

    const {
        isAdding,
        isCheckingDate,
        dateIsAvailable,
        addToBasket,
        checkDateAvailability,
        setIsAdding,
        setDateIsAvailable,
        setIsCheckingDate,
    } = useAddToBasket({
        id,
        price,
        productName,
        isMenu: !!menuId,
        weightPercentage: foodData?.weight_percentage,
        portionLabel: foodData?.portion_label,
    });

    // Track date string for rendering conditionally
    const [dateStrState, setDateStrState] = useState(() => localStorage.getItem('selectedServiceDate'));

    // Update local state when date changes via storage event
    const refreshDate = useCallback(() => {
        const current = localStorage.getItem('selectedServiceDate');
        setDateStrState(current);
    }, []);
    useStorageEvent(STORAGE_EVENT_DATE, refreshDate);

    const hasIds = foodId != null || menuId != null;

    useEffect(() => {
        if (!showCalendar || !hasIds) return;

        const fetchAvailability = async () => {
            setLoadingDates(true);
            try {
                const { data } = await api.post<{ dates: string[] }>(API.AVAILABILITY, {
                    food_ids: foodId ? [foodId] : undefined,
                    menu_id: menuId,
                });
                setAvailableDates(new Set(data.dates ?? []));
            } catch {
                setAvailableDates(new Set());
            } finally {
                setLoadingDates(false);
            }
        };

        fetchAvailability();
    }, [showCalendar, foodId, menuId, hasIds]);

    if (!features.order) return null;

    const doAdd = async (overrideDate?: string) => {
        setIsAdding(true);
        const dateToUse = overrideDate ?? localStorage.getItem('selectedServiceDate');
        if (!dateToUse) {
            setShowCalendar(true);
            setIsAdding(false);
            return;
        }

        try {
            const reqQty = compact ? 1 : quantity;
            const canAdd = await checkDateAvailability(dateToUse, reqQty);
            if (canAdd) {
                addToBasket(reqQty, dateToUse);
                setQuantity(minQuantity);
            }
        } catch {
            showToast('error', cartLabels.validation_error_label);
        } finally {
            setIsAdding(false);
        }
    };

    const handleDecrement = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setQuantity((q) => Math.max(minQuantity, q - 1));
        trackEvent('menu_quantity_decrease', { item_id: id, item_name: productName });
    };

    const handleIncrement = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setQuantity((q) => Math.min(maxQuantity, q + 1));
        trackEvent('menu_quantity_increase', { item_id: id, item_name: productName });
    };

    const handleAdd = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isAvailable || isAdding) return;

        const dateStr = localStorage.getItem('selectedServiceDate');
        if (!dateStr) {
            setShowCalendar(true);
            return;
        }

        doAdd(dateStr);
    };

    const handleDateSelect = async (date: Date) => {
        setTempSelectedDate(date);
        const iso = date.toISOString().split('T')[0];
        const reqQty = compact ? 1 : quantity;
        try {
            await checkDateAvailability(iso, reqQty);
        } catch {
            // dateIsAvailable already set to false by checkDateAvailability
            setDateIsAvailable(false);
        }
    };

    const handleConfirmDate = () => {
        if (!tempSelectedDate || !dateIsAvailable) return;
        const iso = tempSelectedDate.toISOString().split('T')[0];
        localStorage.setItem('selectedServiceDate', iso);
        window.dispatchEvent(new CustomEvent(STORAGE_EVENT_DATE));
        setShowCalendar(false);
        setTempSelectedDate(null);
        doAdd(iso);
    };

    const buttons = compact ? (
        <AddToBasketCompact disabled={!isAvailable} validating={isAdding} onClick={handleAdd} />
    ) : (
        <AddToBasketFull
            quantity={quantity}
            minQuantity={minQuantity}
            maxQuantity={maxQuantity}
            disabled={!isAvailable}
            validating={isAdding}
            hasDate={!!dateStrState}
            onDecrement={handleDecrement}
            onIncrement={handleIncrement}
            onAdd={handleAdd}
        />
    );

    return (
        <>
            {buttons}
            {showCalendar && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                    onClick={() => {
                        setShowCalendar(false);
                        setTempSelectedDate(null);
                    }}
                >
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                    <div
                        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-5 max-w-xs w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="text-sm font-medium text-app-text mb-3">{cartLabels.select_date_label}</p>
                        {loadingDates ? (
                            <div className="flex items-center justify-center py-12">
                                <SpinnerIcon className="w-8 h-8 text-app-primary" />
                            </div>
                        ) : (
                            <>
                                <Calendar
                                    selected={tempSelectedDate}
                                    onSelect={handleDateSelect}
                                    minDate={new Date()}
                                    availableIsoDates={availableDates}
                                />
                                {tempSelectedDate && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            fullWidth
                                            onClick={handleConfirmDate}
                                            disabled={isCheckingDate || !dateIsAvailable}
                                        >
                                            {isCheckingDate ? (
                                                <SpinnerIcon />
                                            ) : foodId ? (
                                                cartLabels.add_button
                                            ) : (
                                                cartLabels.add_button
                                            )}
                                        </Button>
                                        {!isCheckingDate && !dateIsAvailable && (
                                            <p className="text-xs text-red-500 text-center mt-2">
                                                {cartLabels.unavailable_message}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default AddToBasket;
